import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { TableCellAction, TableCellActionData } from "@app/models";
import { SortEvent } from "@app/shared/directives";
import { CURRENCY_DIGITS_INFO, CURRENCY_DISPLAY, DEFAULT_CURRENCY_CODE } from "@app/shared/utils";
import { ApiListResponseModel, PageModel, PageParams, SortParamsModel } from "clearline-api";
import { AbstractGridComponent } from "../abstract-grid.component";

@Component({
  selector: "app-paginated-grid",
  templateUrl: "./paginated-grid.component.html",
  styleUrls: ["./paginated-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginatedGridComponent<T extends { id: number | string } & Record<string, unknown>> extends AbstractGridComponent<T> {
  @Input() search = "";

  @Input() set data(value: ApiListResponseModel<T[]>) {
    if (!!value) {
      const { data = [], page: pageData, sort } = value;
      const { page, pageSize, total } = pageData;

      this.selectionMap.clear();
      this.tableData = this.normalizeTableData(this.columnData, data, this.datePipe, this.columnDateFormat);
      this.pagination = { page, pageSize, total };
    }
  }

  @Output() pageChange = new EventEmitter<PageParams>();
  @Output() sortChange = new EventEmitter<SortParamsModel>();
  @Output() tableAction = new EventEmitter<TableCellActionData<T>>();

  protected tableData: T[] = [];
  protected pagination: PageModel = { page: 1, pageSize: 10, total: 0 };
  protected sorts: SortParamsModel = { field: "", direction: "asc" };
  protected readonly defaultCurrencyCode = DEFAULT_CURRENCY_CODE;
  protected readonly currencyDisplay = CURRENCY_DISPLAY;
  protected readonly currencyDigitsInfo = CURRENCY_DIGITS_INFO;

  constructor(protected datePipe: DatePipe) {
    super(datePipe);
  }

  onSort({ column, direction }: SortEvent) {
    this.sorts = { field: column, direction: direction ? direction : "none" };
    this.sortChange.emit(this.sorts);
  }

  onPageChange(page: number) {
    this.pageChange.emit({
      page,
      pageSize: this.pagination.pageSize
    });
  }

  onPageSizeChange(size: number) {
    this.pageChange.emit({
      page: 1, // reset to first page on page size change
      pageSize: size
    });
  }

  onTableAction(action: TableCellAction, item: T): void {
    this.tableAction.emit({ action, item });
  }

  trackByRowId = (_: number, row: T): number | string => row.id;
}
